import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { InfoCircleOutlined, QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { OpenTradeAccount } from 'features/subscription-freeze/openTrades';
import { PAUSE_REVIEW_STEP } from '../content';
import { Callout } from './StepParts';

/** One account per row, under an optional lead saying what the list is. */
function AccountNames({ accounts, lead }: { accounts: OpenTradeAccount[]; lead?: string }) {
  const list = (
    <Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0, mt: lead ? 0.5 : 1 }}>
      {accounts.map((account) => (
        <Typography key={account.accountId} component="li" variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
          {account.label}
        </Typography>
      ))}
    </Stack>
  );
  if (!lead) return list;
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
        {lead}
      </Typography>
      {list}
    </>
  );
}

function Body({ children }: { children: string }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}

type Props = {
  loading: boolean;
  /** The account list could not be read, so no account can be named. */
  failed: boolean;
  accounts: OpenTradeAccount[];
  /** Accounts whose own check failed. */
  unchecked: OpenTradeAccount[];
};

/** A pause never closes a trade and copied trades lose copying and protection while paused, so the review
 *  names every account still holding one, and every account it could not check. */
export default function PauseOpenTrades({ loading, failed, accounts, unchecked }: Props) {
  if (loading) {
    return (
      <Stack direction="row" spacing={1.25} alignItems="center" role="status">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          {PAUSE_REVIEW_STEP.checkingTrades}
        </Typography>
      </Stack>
    );
  }
  if (failed) {
    return (
      <Callout icon={<WarningOutlined />} tone="error" title={PAUSE_REVIEW_STEP.tradesUncheckedTitle}>
        <Body>{PAUSE_REVIEW_STEP.tradesUncheckedBody}</Body>
      </Callout>
    );
  }
  if (!accounts.length && !unchecked.length) {
    return (
      <Callout icon={<InfoCircleOutlined />} tone="primary">
        <Body>{PAUSE_REVIEW_STEP.noCopiedTrades}</Body>
      </Callout>
    );
  }

  return (
    <Stack spacing={1.5}>
      {accounts.length > 0 && (
        <Callout icon={<WarningOutlined />} tone="error" title={PAUSE_REVIEW_STEP.openTradesTitle(accounts.length)}>
          <AccountNames accounts={accounts} lead={PAUSE_REVIEW_STEP.openTradesLead} />
          <Box sx={{ mt: 1 }}>
            <Body>{PAUSE_REVIEW_STEP.openTradesBody}</Body>
          </Box>
        </Callout>
      )}
      {unchecked.length > 0 && (
        <Callout icon={<QuestionCircleOutlined />} tone="warning" title={PAUSE_REVIEW_STEP.someUncheckedTitle(unchecked.length)}>
          <Body>{PAUSE_REVIEW_STEP.someUncheckedBody}</Body>
          <AccountNames accounts={unchecked} />
        </Callout>
      )}
    </Stack>
  );
}
